
/* Modulo: Reviews Overlay — optimized review card rail.
   Motion 8.1 compatible.
   Avoids deliberate forced reflows and caches layout measurements.
   Supports reduced motion, autoplay, swipe, and Shopify theme editor.
*/
(function () {
  'use strict';

  if (typeof window === 'undefined') return;

  var REDUCE = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  function initSection(el) {
    if (!el || el.__toInit) return;

    var track = el.querySelector('[data-modulo-to-track]');
    if (!track) return;

    el.__toInit = true;

    var prev = el.querySelector('[data-modulo-to-prev]');
    var next = el.querySelector('[data-modulo-to-next]');

    var stepMode =
      el.getAttribute('data-step-mode') === 'single'
        ? 'single'
        : 'page';

    var page = 0;
    var timer = null;
    var resizeTimer = null;
    var transitionFrame = null;
    var resizeFrame = null;
    var destroyed = false;

    var autoplayOn =
      el.getAttribute('data-autoplay') === 'true' && !REDUCE;

    var speed = parseFloat(el.getAttribute('data-autoplay-speed'));
    if (!speed || isNaN(speed)) speed = 6;

    // Cached layout measurements.
    var cardWidth = 0;
    var gap = 0;
    var totalCards = 0;
    var metricsReady = false;

    function cards() {
      return track.querySelectorAll('.modulo-to__card');
    }

    function viewportWidth() {
      return window.innerWidth ||
        document.documentElement.clientWidth;
    }

    function cardsPerView() {
      var w = viewportWidth();
      var attr;

      if (w <= 480) {
        attr = 'data-cpv-mobile';
      } else if (w <= 860) {
        attr = 'data-cpv-tablet';
      } else {
        attr = 'data-cpv-desktop';
      }

      var n = parseInt(el.getAttribute(attr), 10);
      return n && !isNaN(n) ? n : 3;
    }

    function applyCpv() {
      el.style.setProperty(
        '--modulo-to-cpv',
        cardsPerView()
      );
    }

    // Read layout only when initializing or recalculating
    // after a resize, not on every carousel movement.
    function measureLayout() {
      if (destroyed || !el.isConnected) return;

      var first = track.querySelector('.modulo-to__card');

      if (!first) {
        cardWidth = 0;
        gap = 0;
        totalCards = 0;
        metricsReady = true;
        return;
      }

      cardWidth = first.getBoundingClientRect().width;

      var styles = window.getComputedStyle(track);
      gap = parseFloat(styles.columnGap || styles.gap) || 0;

      totalCards = cards().length;
      metricsReady = true;
    }

    function pageCount() {
      var cpv = cardsPerView();

      if (totalCards <= cpv) return 1;

      if (stepMode === 'single') {
        return Math.max(1, totalCards - cpv + 1);
      }

      return Math.max(1, Math.ceil(totalCards / cpv));
    }

    // Restore the original inline transition after an instant jump.
    // No offsetHeight or other forced layout read is needed.
    function jumpWithoutAnimation(offset) {
      var previousTransition = track.style.transition;

      if (transitionFrame !== null) {
        window.cancelAnimationFrame(transitionFrame);
        transitionFrame = null;
      }

      track.style.transition = 'none';
      track.style.transform =
        'translate3d(' + (-offset) + 'px, 0, 0)';

      transitionFrame = window.requestAnimationFrame(function () {
        transitionFrame = null;

        if (destroyed) return;

        track.style.transition = previousTransition;
      });
    }

    function goTo(p, animate) {
      if (destroyed || !metricsReady) return;

      var pages = pageCount();
      page = clamp(p, 0, pages - 1);

      var cpv = cardsPerView();
      var stride = stepMode === 'single' ? 1 : cpv;
      var maxCards = Math.max(0, totalCards - cpv);
      var advance = Math.min(page * stride, maxCards);
      var offset = advance * (cardWidth + gap);

      if (!animate || REDUCE) {
        jumpWithoutAnimation(offset);
      } else {
        if (transitionFrame !== null) {
          window.cancelAnimationFrame(transitionFrame);
          transitionFrame = null;
        }

        track.style.transform =
          'translate3d(' + (-offset) + 'px, 0, 0)';
      }

      if (prev) prev.disabled = page === 0;
      if (next) next.disabled = page >= pages - 1;
    }

    // Autoplay.
    function startAutoplay() {
      if (destroyed || !autoplayOn || timer) return;

      timer = window.setInterval(function () {
        if (destroyed || !el.isConnected) {
          stopAutoplay();
          return;
        }

        var pages = pageCount();
        goTo(page + 1 >= pages ? 0 : page + 1, true);
      }, speed * 1000);
    }

    function stopAutoplay() {
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }

      autoplayOn = false;
    }

    // Navigation handlers.
    function onPrev() {
      stopAutoplay();
      goTo(page - 1, true);
    }

    function onNext() {
      stopAutoplay();
      goTo(page + 1, true);
    }

    if (prev) prev.addEventListener('click', onPrev);
    if (next) next.addEventListener('click', onNext);

    // Touch and pen swipe.
    var SWIPE = 40;
    var startX = null;
    var startY = null;

    function onDown(e) {
      if (e.pointerType !== 'touch' &&
          e.pointerType !== 'pen') return;

      startX = e.clientX;
      startY = e.clientY;
    }

    function onUp(e) {
      if (startX === null) return;

      var dx = e.clientX - startX;
      var dy = e.clientY - startY;

      if (Math.abs(dx) > SWIPE &&
          Math.abs(dx) > Math.abs(dy)) {
        stopAutoplay();
        goTo(dx < 0 ? page + 1 : page - 1, true);
      }

      startX = null;
      startY = null;
    }

    function onCancel() {
      startX = null;
      startY = null;
    }

    track.addEventListener('pointerdown', onDown);
    track.addEventListener('pointerup', onUp);
    track.addEventListener('pointercancel', onCancel);

    // Pause autoplay while the pointer is over the section.
    function onEnter() {
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    function onLeave() {
      startAutoplay();
    }

    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);

    // Resize: debounce, apply responsive settings, then
    // recalculate measurements in the next animation frame.
    function onResize() {
      if (destroyed || !el.isConnected) return;

      window.clearTimeout(resizeTimer);

      resizeTimer = window.setTimeout(function () {
        if (destroyed) return;

        applyCpv();

        if (resizeFrame !== null) {
          window.cancelAnimationFrame(resizeFrame);
        }

        resizeFrame = window.requestAnimationFrame(function () {
          resizeFrame = null;

          if (destroyed) return;

          measureLayout();
          goTo(Math.min(page, pageCount() - 1), false);
        });
      }, 100);
    }

    window.addEventListener('resize', onResize, { passive: true });

    // Shopify theme-editor teardown.
    el.__toTeardown = function () {
      if (destroyed) return;
      destroyed = true;

      stopAutoplay();
      window.clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);

      if (transitionFrame !== null) {
        window.cancelAnimationFrame(transitionFrame);
        transitionFrame = null;
      }

      if (resizeFrame !== null) {
        window.cancelAnimationFrame(resizeFrame);
        resizeFrame = null;
      }

      if (prev) prev.removeEventListener('click', onPrev);
      if (next) next.removeEventListener('click', onNext);

      track.removeEventListener('pointerdown', onDown);
      track.removeEventListener('pointerup', onUp);
      track.removeEventListener('pointercancel', onCancel);

      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);

      el.__toInit = false;
      delete el.__toTeardown;
    };

    // Initialize after the browser has had an opportunity
    // to apply the section's responsive CSS.
    applyCpv();

    window.requestAnimationFrame(function () {
      if (destroyed || !el.isConnected) return;

      measureLayout();
      goTo(0, false);
      startAutoplay();
    });
  }

  function initAll(root) {
    var scope =
      root && root.querySelectorAll ? root : document;

    if (scope.matches && scope.matches('[data-modulo-to]')) {
      initSection(scope);
    }

    var els = scope.querySelectorAll('[data-modulo-to]');

    for (var i = 0; i < els.length; i++) {
      initSection(els[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Shopify theme-editor lifecycle.
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target;
    if (!el) return;

    if (el.matches && el.matches('[data-modulo-to]')) {
      if (el.__toTeardown) el.__toTeardown();
      initSection(el);
    } else {
      initAll(el);
    }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target;
    if (!el) return;

    if (el.matches && el.matches('[data-modulo-to]')) {
      if (el.__toTeardown) el.__toTeardown();
    } else if (el.querySelectorAll) {
      var inner = el.querySelectorAll('[data-modulo-to]');

      for (var i = 0; i < inner.length; i++) {
        if (inner[i].__toTeardown) {
          inner[i].__toTeardown();
        }
      }
    }
  });
})();
